export enum Route {
  dashboard = "/dashboard",
  clients = "/clients",
  reviews = "/reviews",
  touchpoints = "/touchpoints",
  approved = "/approved",
  admin = "/admin",
  new = "new",
  edit = "edit",
  recentCreatives = "recent-creatives",
  campaigns = "campaigns",
  settings = "settings",
  strategy = "strategy",
  creative = "creative",
  profile = "profile",
  assets = "assets",
  theme = "theme",
  brandIdentity = "brand-identity",
  configuration = "configuration",
  approvalQueue = "approval-queue",
  batch = "batch",
  folder = "folder",
  smsSimulator = "/sms-simulator",
  rules = "rules",
  personas = "personas",
  simulator = "simulator",
  notFound = "/404",
  emails = "emails",
  landingPages = "landing-pages",
  emptyJourneyNodes = "empty-journey-nodes",
  emailBuilder = "email-builder",
  emailPreview = "email-preview",
  lpBuilder = "landing-page-builder",
  lpPreview = "landing-page-preview",
  login = "/login",
  logout = "/logout",
  versions = "versions",
  themeBuilder = "theme-builder",
  themePreview = "theme-preview",
  details = "details",
  preview = "preview",
  basic = "basic",
  team = "team-members",
  emailTypes = "email-types",
  design = "design",
  programTemplates = "program-templates",
  campaignTemplates = "campaign-templates",
  campaignWriterRules = "campaign-writer-rules",
  programs = "programs",
  partner = "partner",
  collegeProfile = "college-profile",
  brandGuidelines = "brand-content-guidelines",
  a2a = "a2a",
  a2e = "a2e",
  studentRouter = "student-router",
}

const buildPathSegments = (...segments: (string | undefined)[]) =>
  segments
    .filter(Boolean)
    .map((segment) => segment?.replace(/^\/+/, "").replace(/\/+$/, ""))
    .join("/")
    .replace(/^\/+/, "");

export const buildPath = (...segments: (string | undefined)[]) =>
  "/" + buildPathSegments(...segments);

export const buildRelativePath = (...segments: (string | undefined)[]) =>
  buildPathSegments(...segments);

export const getClientPaths = ({ clientId }: { clientId?: string } = {}) => {
  const clientPath = buildPath(Route.clients, clientId ?? ":clientId");
  const adminPath = buildPath(Route.admin);

  return {
    clientRecentCreativesPath: buildPath(clientPath, Route.recentCreatives),
    clientCampaignsPath: buildPath(clientPath, Route.campaigns),
    clientProgramsPath: buildPath(clientPath, Route.programs),
    clientProgramsA2APath: buildPath(clientPath, Route.programs, Route.a2a),
    clientProgramsA2EPath: buildPath(clientPath, Route.programs, Route.a2e),
    approvalQueuePath: buildPath(clientPath, Route.approvalQueue),
    clientThemePath: buildPath(clientPath, Route.theme),
    clientThemeBrandIdentityPath: buildPath(clientPath, Route.theme, Route.brandIdentity),
    clientThemePreviewPath: buildPath(clientPath, Route.theme, Route.preview),
    clientThemeConfigurationPath: buildPath(clientPath, Route.theme, Route.configuration),
    clientProgramTemplatesPath: buildPath(clientPath, Route.programTemplates),
    clientCampaignTemplatesPath: buildPath(adminPath, Route.campaignTemplates),
    clientCampaignWriterRulesPath: buildPath(adminPath, Route.campaignWriterRules),
    clientProfilePath: buildPath(clientPath, Route.profile),
    clientPartnerPath: buildPath(clientPath, Route.partner),
    clientCollegeProfilePath: buildPath(clientPath, Route.collegeProfile),
    clientBrandGuidelinesPath: buildPath(clientPath, Route.brandGuidelines),
    clientStudentRouterPath: buildPath(clientPath, Route.studentRouter),
  } as const;
};

export const getCampaignPaths = ({ clientId = ":clientId", campaignId = ":campaignId" } = {}) => {
  const campaignPath = buildPath(Route.clients, clientId, Route.campaigns, campaignId);

  return {
    campaignCreativePath: buildPath(campaignPath, Route.creative),
    campaignSettingsPath: buildPath(campaignPath, Route.settings),
    campaignStrategyPath: buildPath(campaignPath, Route.strategy),
    campaignSettingsEditPath: buildPath(campaignPath, Route.settings, Route.edit),
    folderDetailsPath: (folderId: string) =>
      buildPath(campaignPath, Route.creative, Route.folder, folderId),
  } as const;
};

export const getTouchpointPaths = ({
  clientId = ":clientId",
  campaignId = ":campaignId",
  touchpointId = ":touchpointId",
} = {}) => {
  const touchpointPath = buildPath(
    Route.clients,
    clientId,
    Route.campaigns,
    campaignId,
    Route.creative,
    touchpointId,
  );

  return {
    touchpointPreviewPath: buildPath(touchpointPath, Route.preview),
    touchpointDetailsPath: buildPath(touchpointPath, Route.details),
    touchpointVersionsPath: buildPath(touchpointPath, Route.versions),
    touchpointPath,
  } as const;
};

/** Query param for deep-linking to a specific touchpoint version (e.g. from approval batch). */
export const TOUCHPOINT_PREVIEW_VERSION_ID_QUERY = "versionId";

export const buildTouchpointPreviewHref = (
  previewPath: string,
  touchpointVersionId?: string | null,
) => {
  if (!touchpointVersionId) {
    return previewPath;
  }
  const params = new URLSearchParams();
  params.set(TOUCHPOINT_PREVIEW_VERSION_ID_QUERY, touchpointVersionId);
  return `${previewPath}?${params.toString()}`;
};

export const getBatchPath = ({
  clientId = ":clientId",
  batchId = ":batchId",
  isClientUser = false,
}) => {
  if (isClientUser) {
    return buildPath(Route.reviews, Route.approvalQueue, Route.batch, batchId);
  }
  const { approvalQueuePath } = getClientPaths({ clientId });
  return buildPath(approvalQueuePath, Route.batch, batchId);
};
